import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Autocomplete,
    TextField,
    FormControl,
    InputAdornment,
    OutlinedInput
} from "@mui/material";
import RemoveIcon from '@mui/icons-material/Remove';
import './EstimateView.css';
import { sortByString } from '../../utils/sortByString';
import { formatMoney, roundMoney, formatCurrencyDisplay } from '../../utils/moneyFormat';
import { mapCompanyToBotmarkInfo } from '../../utils/botmarkCompanyInfo';
import {
    EMPTY_CUSTOMER_COMPANY_ID,
    buildBotmarkEstimateInvoice,
    getInvoiceChargedRate,
    hasEstimateReferencePlan,
    isInternalEstimateCustomer,
} from '../../utils/buildBotmarkEstimateInvoice';
import {
    getEstimateGrossTotal,
    isBelowConfiguredBillingRate,
    isMissingCreditDescription,
} from '../../utils/estimateOverrideUtils';
import { useBotmarkCompanyInfo } from '../../hooks/useCompanies';
import { useCreateInvoice, useReferenceEstimates } from '../../hooks/useInvoices';
import { isFileOptionEqual } from '../../query/queryConfig';

function BotmarkEstimateDataView({
    specialPointTypeTotal,
    pointTypeTotal,
    botmarkEstimateObj,
    company,
    companyData,
    customer,
    selectedCustomer,
    userData,
    handleCloseBotmarkEstimate,
    baseServiceFee,
    managePlans,
}) {
    const createInvoice = useCreateInvoice();
    const { data: botmarkCompany } = useBotmarkCompanyInfo();
    const botmarkInfo = mapCompanyToBotmarkInfo(botmarkCompany);
    const creditCompany = managePlans ? companyData : company;
    const canApplyCredit = creditCompany?.creditEnabled === true;

    const data = JSON?.parse(botmarkEstimateObj?.botmarkInvoiceValues);
    const [planCredit, setPlanCredit] = React.useState(0);
    const [maxCreditAmount, setMaxCreditAmount] = React.useState(0);
    const [creditReason, setCreditReason] = React.useState("");
    const [selectedFile, setSelectedFile] = React.useState(null);

    const hasReferencePlan = hasEstimateReferencePlan(selectedFile);

    const companyId = managePlans ? companyData?.id : company?.id;
    const referenceCustomerId = customer !== EMPTY_CUSTOMER_COMPANY_ID ? customer : companyId;
    const { data: CompanyInvoices = [] } = useReferenceEstimates({
        companyId,
        customerId: referenceCustomerId,
        enabled: Boolean(companyId && referenceCustomerId),
    });

    let allFilesArray = sortByString(CompanyInvoices ?? [], 'fileName');

    const defaultProps = {
        options: allFilesArray,
        getOptionLabel: (option) => option.fileName ?? '',
        isOptionEqualToValue: isFileOptionEqual,
        // MUI list keys default to the label; duplicate file names collide. Key by id, search by name.
        renderOption: (props, option) => (
            <li {...props} key={option.fileId}>
                {option.fileName}
            </li>
        ),
    };

    function addReferencePlan(event){
        setSelectedFile(event);

        if (!hasEstimateReferencePlan(event)) {
            setMaxCreditAmount(0);
            return;
        }

        const itemData = JSON.parse(event.invoiceValues);
        let pointData;
        if (event.pointList) {
            pointData = JSON.parse(event.pointList);
        }

        let thousandPoints = [];
        let thousandPoint = 0;
        let BMPoints = [];
        let BMTotal = 0;
        let EPoint = 0;
        let CPoint = 0;
        let UPoint = 0;
        let CLPoints = [];
        let CLTotal = 0;
        let AllPointTotal = 0;

        for (let i = 0; i < pointData?.length; i++) {
            if (pointData[i]?.TypeEnum == 0){
                thousandPoints.push(pointData[i]);
                thousandPoint = pointData[i]?.Quantity;
            }
            else if (pointData[i]?.TypeEnum == 1){
                EPoint = pointData[i]?.Quantity;
            }
            else if (pointData[i]?.TypeEnum == 2){
                CPoint = pointData[i]?.Quantity;
            }
            else if (pointData[i]?.TypeEnum == 3){
                UPoint = pointData[i]?.Quantity;
            }
            else if ([1, 2, 3].includes(pointData[i].TypeEnum)){
                BMPoints.push(pointData[i])
                BMTotal += pointData[i]?.Quantity;
            }
            else if ((![0, 1, 2, 3].includes(pointData[i].TypeEnum)) && pointData[i]?.Enabled == true){
                CLPoints.push(pointData[i])
                CLTotal += pointData[i]?.Quantity;
            }
            if (pointData[i]?.Enabled == true){
                AllPointTotal += pointData[i]?.Quantity;
            }
        }

        setMaxCreditAmount(Number(itemData.finalTotal));

        if (CLTotal != 0) {
            setCreditReason(
                `#${itemData.estimateNumber} | ${itemData.date} \npoints total: ${AllPointTotal} \n1000:${thousandPoint},E:${EPoint},C:${CPoint},U:${UPoint} CL: ${CLTotal}`
            );
        } else {
            setCreditReason(
                `#${itemData.estimateNumber} | ${itemData.date} \npoints total: ${AllPointTotal} \n1000:${thousandPoint},E:${EPoint},C:${CPoint},U:${UPoint}`
            );
        }
    }

    const variableCompanyData = managePlans ? companyData : company;
    const isInternalUse = isInternalEstimateCustomer(selectedCustomer);
    const displayRate = getInvoiceChargedRate({
        processorCompany: variableCompanyData,
        selectedCustomer,
    });

    const referenceData = hasReferencePlan
        ? JSON?.parse(selectedFile.invoiceValues)
        : {};

    const invoiceData = buildBotmarkEstimateInvoice({
        estimateData: data,
        isProcessed: botmarkEstimateObj.isProcessed,
        specialPointTypeTotal,
        pointTypeTotal,
        planCredit,
        selectedFile,
        referenceData,
        creditReason,
        baseServiceFee,
        selectedCustomer,
        variableCompanyData,
        userData,
        botmarkInfo,
    });

    let sendInvoiceObject = {
        Id: botmarkEstimateObj.botmarkEstimateId,
        CustomerId: botmarkEstimateObj.botmarkInvoiceDeveloperId,
        DeveloperId: botmarkEstimateObj.botmarkInvoiceDeveloperId,
        InvoiceValues: JSON.stringify(invoiceData),
        OpenInvoice: true,
        JobId: botmarkEstimateObj.id,
        isSent: true,
        isInvoiceAccepted: parseInt(2)
    };
    let sendForCustomerInvoiceObject = {
        Id: botmarkEstimateObj.botmarkEstimateId,
        CustomerId: botmarkEstimateObj.botmarkInvoiceCustomerId,
        DeveloperId: botmarkEstimateObj.botmarkInvoiceDeveloperId,
        InvoiceValues: JSON.stringify(invoiceData),
        OpenInvoice: true,
        JobId: botmarkEstimateObj.id,
        isSent: true,
        isInvoiceAccepted: parseInt(4)
    };

    async function submitAcceptedEstimate(invoiceObject) {
        await createInvoice.mutateAsync({
            invoiceObject,
            JobId: botmarkEstimateObj.id,
        });
        handleCloseBotmarkEstimate();
    }

    function estimateAcceptanceLogic(){
        if (isMissingCreditDescription(planCredit, creditReason)) {
            swal({
                title: "MISSING INFORMATION",
                text: `To accept estimate with credit applied, please provide description.`,
                icon: "warning",
                buttons: false,
                dangerMode: true,
            });
            return;
        }

        if (isInternalUse) {
            swal({
                title: "ACCEPT ESTIMATE",
                text: `Estimate is being accepted for ${variableCompanyData?.name}'s Internal Use, once accepted this cannot be changed. Is this Correct?`,
                icon: "warning",
                buttons: true,
                dangerMode: false,
            })
            .then((willUpdate) => {
                if (willUpdate) {
                    submitAcceptedEstimate(sendInvoiceObject);
                } else {
                    swal({
                        title: `Estimate not Accepted.`
                    });
            }})
        } else {
            swal({
                title: "ACCEPT ESTIMATE",
                text: `Estimate is being accepted for ${selectedCustomer.companyContact}, once accepted this cannot be changed. Is this Correct?`,
                icon: "warning",
                buttons: true,
                dangerMode: false,
            })
            .then((willUpdate) => {
                if (willUpdate) {
                    submitAcceptedEstimate(sendForCustomerInvoiceObject);
                } else {
                    swal({
                        title: `Estimate Not Accepted.`
                    });
            }})
        }
    };

    function setCreditAmount(event) {
        const grossTotal = getEstimateGrossTotal({
            isProcessed: botmarkEstimateObj.isProcessed,
            pointTypeTotal,
            specialPointTypeTotal,
            displayRate,
            baseServiceFee,
        });
        const nextCredit = Number(event);
        // Allow entry freely (including below base service fee). Description is required on Accept.
        // Only prevent a negative estimate total while typing.
        if (roundMoney(grossTotal - nextCredit) < 0) {
            setPlanCredit(formatMoney(grossTotal));
        } else {
            setPlanCredit(event);
        }
    };

    return (
        <div>
            <Table>
                <TableHead>
                    <TableRow><TableCell colSpan={6}></TableCell></TableRow>
                    <TableRow>
                        <TableCell className="estimateTableCellVariant">Item</TableCell>
                        <TableCell className="estimateTableCellVariant">Description</TableCell>
                        <TableCell className="estimateTableCellVariant">Qty</TableCell>
                        <TableCell className="estimateTableCellVariant">U/M</TableCell>
                        <TableCell className="estimateTableCellVariant">Rate</TableCell>
                        <TableCell className="estimateTableCellVariant">Amount</TableCell>
                    </TableRow>
                </TableHead>
                    <TableBody>
                    { botmarkEstimateObj.isProcessed == 1 ?
                        <TableRow>
                            <TableCell className="estimateTableCellVariant">BM Points</TableCell>
                            <TableCell className="estimateTableCellVariant">E+C+U</TableCell>
                            <TableCell className="estimateTableCellVariant">{pointTypeTotal}</TableCell>
                            <TableCell className="estimateTableCellVariant">per/point</TableCell>
                            <TableCell className="estimateTableCellVariant">{displayRate}</TableCell>
                            <TableCell className="estimateTableCellVariant">
                                {formatCurrencyDisplay(Number(pointTypeTotal) * displayRate)}
                            </TableCell>
                        </TableRow>
                        :
                        <TableRow>
                            <TableCell className="estimateTableCellVariant"><b>Botmark Base Service Fee</b></TableCell>
                            <TableCell className="estimateTableCellVariant"><span>Manual Processing</span></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant">
                                {formatCurrencyDisplay(Number(baseServiceFee))}
                            </TableCell>
                        </TableRow>
                    }
                    { specialPointTypeTotal != 0 &&
                        <TableRow>
                        <TableCell className="estimateTableCellVariant">CL Points</TableCell>
                        <TableCell className="estimateTableCellVariant">Chargeable Special Points</TableCell>
                        <TableCell className="estimateTableCellVariant">{specialPointTypeTotal}</TableCell>
                        <TableCell className="estimateTableCellVariant">per/point</TableCell>
                        <TableCell className="estimateTableCellVariant">{displayRate}</TableCell>
                        <TableCell className="estimateTableCellVariant">
                            {formatCurrencyDisplay(Number(specialPointTypeTotal) * displayRate)}
                        </TableCell>
                    </TableRow>
                    }
                    {canApplyCredit &&
                        <TableRow>
                            <TableCell className="estimateTableCellVariant" style={{width:'300px'}}>
                                <form style={{display:'flex', flexDirection:'column'}}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            disablePortal
                                            value={selectedFile}
                                            {...defaultProps}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    label="Apply Credit (optional)"
                                                />}
                                            onChange={(event,newValue) => {
                                                addReferencePlan(newValue ?? null);
                                            }}
                                        />
                                    </FormControl>
                                </form>
                            </TableCell>
                            <TableCell className="estimateTableCellVariant">
                                <TextField
                                    multiline
                                    fullWidth
                                    placeholder="Required for any credit"
                                    onChange={(event) => setCreditReason(event.target.value)}
                                    value={creditReason}
                                />
                            </TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            {hasReferencePlan ?
                                <TableCell className="estimateTableCellVariant" colSpan={2}>
                                    Reference Total: {maxCreditAmount}
                                </TableCell>
                                :
                                <TableCell className="estimateTableCellVariant" colSpan={2}></TableCell>
                            }
                            <TableCell className="estimateTableCellVariant" style={{width:'10vw'}}>
                                <OutlinedInput
                                    id="outlined-adornment-amount"
                                    startAdornment={<InputAdornment position="start"><RemoveIcon/></InputAdornment>}
                                    onChange={(event) => setCreditAmount(event.target.value)} value={planCredit}
                                />
                            </TableCell>
                        </TableRow>
                    }
                    {botmarkEstimateObj.isProcessed == 1 ?
                        <TableRow>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"><b>Total:</b></TableCell>
                            <TableCell className="estimateTableCellVariant">
                                {formatCurrencyDisplay(((pointTypeTotal + specialPointTypeTotal) * displayRate) - planCredit)}
                            </TableCell>
                        </TableRow>
                        :
                        <TableRow>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"></TableCell>
                            <TableCell className="estimateTableCellVariant"><b>Total:</b></TableCell>
                            <TableCell className="estimateTableCellVariant">
                                {formatCurrencyDisplay(baseServiceFee - planCredit)}
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
                <br />
                <Button onClick={() => handleCloseBotmarkEstimate()}>Back</Button>
                {isBelowConfiguredBillingRate(displayRate) && !canApplyCredit ?
                    <>
                        <Button disabled onClick={() => estimateAcceptanceLogic()}>Accept</Button>
                        <br/>
                        <span>
                            {isInternalUse
                                ? 'Contact Botmark to set up Pricing Rate'
                                : 'Contact Botmark to set up Pricing Rate for Customer'}
                        </span>
                    </>
                    :
                    <Button onClick={() => estimateAcceptanceLogic()}>Accept</Button>
                }
        </div>
    )
}

export default BotmarkEstimateDataView;
